import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useParams } from "react-router";
import { toast } from "react-toastify";
import {
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Typography,
  Box,
  Stack,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import axios from "axios";

import { useEcrfContext } from "../../context/EcrfContext";
import useSpecialVisitSessions from "../../hooks/useSpecialVisitSessions";
import useChangeVisitStatusMutation from "../../hooks/useChangeVisitStatusMutation";
import AllBlocksAccordion from "../TrialAccordions/AllBlocksAccordion";
import { CustomDatePicker } from "../UI/CustomDatePicker";
import { __API_URL__ } from "../../SERVER_URL";
import { USER_ROLES } from "../../utils/constants";
import { formatDate, toISOString } from "../../utils/helpers";

dayjs.extend(utc);

const headerCellSx = {
  backgroundColor: (theme) => theme.palette.grey[100],
  fontWeight: "bold",
  textTransform: "uppercase",
  fontSize: "0.85rem",
  color: "#424242",
  whiteSpace: "nowrap",
};

const bodyCellSx = {
  whiteSpace: "nowrap",
};

const SpecialVisitSession = ({
  selectedBlock,
  setSelectedBlock,
  handleSelectBlock,
  allSurveys,
  setMessage,
  setTypeOfMessage,
  modalVisitChangeShow,
  setModalVisitChangeShow,
  searchTrailType,
  setSearchTrailType,
  searchBlockName,
  setSearchBlockName,
  newForm,
  setNewForm,
  setHasUnsavedEcrfChanges,
  handleVisitUpdate,
}) => {
  const token = localStorage.getItem("token");
  const user_data = JSON.parse(localStorage.getItem("user_data"));
  const role = user_data.role;

  const { _id_ } = useParams();
  const queryClient = useQueryClient();

  const {
    selectedVisit,
    setSelectedVisit,
    setSelectedSessionId,
    selectedSessionId,
    patientId,
    trial,
    surveyData,
  } = useEcrfContext();
  const isTrialLocked = ["Completed", "Archived"].includes(trial?.status);

  const { data: photoSessions, refetch: refetchSpecialVisitSessions } = useSpecialVisitSessions({
    trialId: trial?.id,
    patientId,
    visitNumber: selectedVisit?.visit_number,
    enabled:
      !!trial?.id &&
      !!patientId &&
      selectedVisit?.visit_number !== undefined &&
      (selectedVisit?.visit_number === -1 ||
        selectedVisit?.visit_number === -2 ||
        selectedVisit?.visit_number === -3),
  });

  const hasAnyInAppActivity = photoSessions?.some((data) => data.has_inapp_activity);

  const hasAnyInClinicActivity = photoSessions?.some((data) => data.has_inclinic_activity);

  const [popMessage, setPopMessage] = useState(false);
  const [documentToDeleteId, setDocumentToDeleteId] = useState();

  const DeleteEvent = (id) => {
    setDocumentToDeleteId(id);
    setPopMessage(true);
  };

  const routeTo = (id) => {
    setSelectedSessionId(id);
    setSelectedVisit((preVisit) => ({
      ...preVisit,
      photo_session: true,
    }));
    const blockNames = surveyData?.map((block) => block.name);
    if (blockNames.includes("Adverse Event Log")) {
      setSelectedBlock("Adverse Event Log");
    } else {
      setSelectedBlock("Patient Activity in this Visit");
    }
  };

  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    setStatusChangePayload((statusChangePayload) => ({
      ...statusChangePayload,
      [name]: value,
    }));
  };

  const [open, setOpen] = useState(false);
  const [statusChangePayload, setStatusChangePayload] = useState({
    inClinicStartDate: dayjs().toISOString(),
    inClinicCompletedDate: null,
    status: "",
    visitNumber: 0,
    trialId: 0,
    sessionId: 0,
    user_selected_name: "",
  });

  const visitNamePlaceholder =
    selectedVisit.visit_number === -1 ? "Adverse Event" : "Unscheduled Visit";

  const createSpecialVisit = (visit_type) => {
    let visit_number;

    if (visit_type === "Unscheduled Event") {
      visit_number = -2;
    } else if (visit_type === "Adverse Event") {
      visit_number = -1;
    } else {
      toast.error("Invalid visit type");
      return;
    }

    setStatusChangePayload({
      status: "InComplete",
      visitNumber: visit_number,
      inClinicStartDate: dayjs().toISOString(),
      inClinicCompletedDate: null,
      sessionId: 0,
      trialId: 0,
      user_selected_name: "",
    });

    axios
      .post(
        `${__API_URL__}/api/photos/special_visit_sessions/`,
        {
          patient_id: patientId,
          visit_type: visit_type,
        },
        {
          headers: {
            Authorization: `Token ${token}`,
          },
        }
      )
      .then((res) => {
        toast.success(`${visit_type} successfully created`);
        setStatusChangePayload((prev) => {
          return {
            ...prev,
            status: "InComplete",
            visitNumber: res.data?.visit_type === "Adverse Event" ? -1 : -2,
            sessionId: res.data.session_id,
          };
        });
        setOpen(true);
        refetchSpecialVisitSessions();
      })
      .catch((err) => {
        toast.error(err.response.data.error);
      });
  };

  const confirmDelete = () => {
    axios
      .delete(`${__API_URL__}/api/photos/special_visit_sessions/`, {
        headers: {
          Authorization: `Token ${token}`,
        },
        data: {
          session_id: documentToDeleteId,
        },
      })
      .then((res) => {
        toast.success("Event deleted successfully");
        refetchSpecialVisitSessions();
        queryClient.invalidateQueries({ queryKey: ["visits"] });
      })
      .catch((err) => {
        toast.error(err.response.data.error);
      })
      .finally(() => {
        setPopMessage(false);
      });
  };

  const changeVisitStatusMutation = useChangeVisitStatusMutation();

  const handleSaveVisitStatus = () => {
    if (!checkStartEndDates()) {
      toast.error("Start Date should be less than End Date");
      return;
    }

    const payload = {
      ...statusChangePayload,
      patientId: _id_,
      trialId: trial?.id,
      user_selected_name: statusChangePayload.user_selected_name,
      inClinicStartDate: toISOString(statusChangePayload.inClinicStartDate),
      inClinicCompletedDate: toISOString(statusChangePayload.inClinicCompletedDate),
    };

    changeVisitStatusMutation.mutate(
      {
        payload: payload,
      },
      {
        onSuccess: () => {
          setOpen(false);
          toast.success("Visit Status Changed Successfully");
        },
        onError: (err) => {
          toast.error(err?.response?.data?.error || "Failed to change visit status");
        },
      }
    );
  };

  const checkStartEndDates = () => {
    if (
      statusChangePayload.status !== "InComplete" &&
      statusChangePayload.inClinicStartDate &&
      statusChangePayload.inClinicCompletedDate &&
      dayjs(statusChangePayload.inClinicStartDate).isAfter(
        dayjs(statusChangePayload.inClinicCompletedDate)
      )
    )
      return false;
    else return true;
  };

  useEffect(() => {
    if (selectedSessionId !== 0) {
      queryClient.invalidateQueries({ queryKey: ["visits"] });
    }
  }, [selectedSessionId]);

  return (
    <>
      {selectedSessionId ? (
        <AllBlocksAccordion
          selectedBlock={selectedBlock}
          setSelectedBlock={setSelectedBlock}
          handleSelectBlock={handleSelectBlock}
          allSurveys={allSurveys}
          setMessage={setMessage}
          setTypeOfMessage={setTypeOfMessage}
          modalVisitChangeShow={modalVisitChangeShow}
          setModalVisitChangeShow={setModalVisitChangeShow}
          newForm={newForm}
          setNewForm={setNewForm}
          searchTrailType={searchTrailType}
          setSearchTrailType={setSearchTrailType}
          searchBlockName={searchBlockName}
          setSearchBlockName={setSearchBlockName}
          setHasUnsavedEcrfChanges={setHasUnsavedEcrfChanges}
          handleVisitUpdate={handleVisitUpdate}
        />
      ) : (
        <Paper elevation={1} sx={{ p: 2, my: 2 }}>
          {(role === USER_ROLES.SUPER_ADMIN ||
            role === USER_ROLES.INVESTIGATOR ||
            role === USER_ROLES.CRC) && (
            <Box display="flex" justifyContent="flex-end" mb={1}>
              {selectedVisit.visit_number === -1 && (
                <Button
                  variant="contained"
                  size="small"
                  sx={{ mx: 1 }}
                  data-testid="create-adverse-event-btn"
                  disabled={isTrialLocked}
                  onClick={() => createSpecialVisit("Adverse Event")}
                >
                  Create Adverse Event
                </Button>
              )}
              {selectedVisit.visit_number === -2 && (
                <Button
                  variant="contained"
                  size="small"
                  sx={{ mx: 1 }}
                  data-testid="create-unscheduled-event-btn"
                  disabled={isTrialLocked}
                  onClick={() => createSpecialVisit("Unscheduled Event")}
                >
                  Create Unscheduled Visit
                </Button>
              )}
            </Box>
          )}

          {photoSessions?.length === 0 ? (
            <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
              There is no existing visit
            </Typography>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={headerCellSx}>Trial Name</TableCell>
                    <TableCell sx={headerCellSx}>Visit Name</TableCell>

                    {hasAnyInClinicActivity && (
                      <>
                        <TableCell sx={headerCellSx}>In-Clinic Visit Start Date</TableCell>
                        <TableCell sx={headerCellSx}>In-Clinic Visit Completed Date</TableCell>
                        <TableCell sx={headerCellSx}>In-Clinic Status</TableCell>
                      </>
                    )}

                    {hasAnyInAppActivity && (
                      <>
                        <TableCell sx={headerCellSx}>App Visit Start Date</TableCell>
                        <TableCell sx={headerCellSx}>App Visit Completed Date</TableCell>
                        <TableCell sx={headerCellSx}>App Status</TableCell>
                      </>
                    )}

                    <TableCell sx={headerCellSx}>Delete</TableCell>
                    <TableCell sx={headerCellSx} />
                    {hasAnyInClinicActivity && <TableCell sx={headerCellSx} />}
                  </TableRow>
                </TableHead>

                <TableBody
                  sx={{
                    "& .MuiTableRow-root:hover": {
                      backgroundColor: "transparent",
                    },
                  }}
                >
                  {photoSessions?.map((session) => (
                    <TableRow key={session?.id}>
                      <TableCell sx={bodyCellSx}>{session?.trial}</TableCell>
                      <TableCell sx={bodyCellSx}>{session?.visit_name}</TableCell>

                      {hasAnyInClinicActivity && (
                        <>
                          <TableCell sx={bodyCellSx}>
                            {formatDate(session?.inclinic_start_date)}
                          </TableCell>
                          <TableCell sx={bodyCellSx}>
                            {formatDate(session?.inclinic_completed_date)}
                          </TableCell>
                          <TableCell sx={bodyCellSx}>
                            {session?.inclinic_visit_status || "-"}
                          </TableCell>
                        </>
                      )}

                      {hasAnyInAppActivity && (
                        <>
                          <TableCell sx={bodyCellSx}>
                            {formatDate(session?.inapp_start_date)}
                          </TableCell>
                          <TableCell sx={bodyCellSx}>
                            {formatDate(session?.inapp_completed_date)}
                          </TableCell>
                          <TableCell sx={bodyCellSx}>
                            {session?.inapp_visit_status || "-"}
                          </TableCell>
                        </>
                      )}

                      <TableCell sx={bodyCellSx}>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => DeleteEvent(session?.id)}
                          disabled={isTrialLocked}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>

                      <TableCell sx={bodyCellSx}>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={session?.id ? () => routeTo(session?.id) : undefined}
                        >
                          View
                        </Button>
                      </TableCell>

                      {hasAnyInClinicActivity && (
                        <TableCell sx={bodyCellSx}>
                          <Button
                            variant="contained"
                            size="small"
                            disabled={isTrialLocked}
                            onClick={() => {
                              setOpen(true);
                              setStatusChangePayload((prev) => ({
                                ...prev,
                                status: session?.inclinic_visit_status || "",
                                visitNumber: session?.visit_type === "Adverse Event" ? -1 : -2,
                                inClinicStartDate: session?.inclinic_start_date || null,
                                inClinicCompletedDate: session?.inclinic_completed_date || null,
                                sessionId: session?.id,
                                user_selected_name:
                                  session?.user_selected_name || session?.visit_name || "",
                              }));
                            }}
                          >
                            Update Status
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <Dialog open={popMessage} onClose={() => setPopMessage(false)} maxWidth="xs" fullWidth>
            <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              Confirm Deletion
            </DialogTitle>
            <DialogContent>
              <DialogContentText>
                Are you sure you want to delete this event? This action is permanent and cannot be
                undone.
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setPopMessage(false)}>Cancel</Button>
              <Button variant="contained" color="error" onClick={confirmDelete}>
                Delete
              </Button>
            </DialogActions>
          </Dialog>

          {open && (
            <Dialog open onClose={() => setOpen(false)} fullWidth maxWidth="sm">
              <DialogTitle>
                Status
                <IconButton
                  onClick={() => setOpen(false)}
                  sx={{ position: "absolute", right: 8, top: 8 }}
                >
                  <CloseIcon />
                </IconButton>
              </DialogTitle>
              <DialogContent>
                <Stack spacing={2} sx={{ mt: 1 }}>
                  <TextField
                    size="small"
                    fullWidth
                    label="Visit Name"
                    name="user_selected_name"
                    placeholder={visitNamePlaceholder}
                    value={statusChangePayload.user_selected_name}
                    onChange={handleFieldChange}
                  />

                  <FormControl size="small" fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      label="Status"
                      value={statusChangePayload.status}
                      SelectDisplayProps={{ "data-testid": "visit-status-select-trigger" }}
                      onChange={(e) => {
                        setStatusChangePayload((prev) => ({
                          ...prev,
                          status: e.target.value,
                        }));
                      }}
                    >
                      <MenuItem value="InComplete">InComplete</MenuItem>
                      <MenuItem value="Skipped">Skipped</MenuItem>
                      <MenuItem value="Completed">Completed</MenuItem>
                    </Select>
                  </FormControl>

                  <CustomDatePicker
                    label="In-Clinic Visit Start Date"
                    variant="outlined"
                    value={statusChangePayload.inClinicStartDate}
                    onChange={(val) => {
                      setStatusChangePayload((prev) => ({
                        ...prev,
                        inClinicStartDate: val,
                      }));
                    }}
                  />

                  <CustomDatePicker
                    label="In-Clinic Visit End Date"
                    variant="outlined"
                    value={statusChangePayload.inClinicCompletedDate}
                    onChange={(val) => {
                      setStatusChangePayload((prev) => ({
                        ...prev,
                        inClinicCompletedDate: val,
                      }));
                    }}
                  />

                  <Box display="flex" justifyContent="flex-end">
                    <Button
                      variant="contained"
                      size="small"
                      data-testid="visit-status-save-btn"
                      onClick={handleSaveVisitStatus}
                    >
                      Save
                    </Button>
                  </Box>
                </Stack>
              </DialogContent>
            </Dialog>
          )}
        </Paper>
      )}
    </>
  );
};

export default SpecialVisitSession;
